import React from 'react';
import { Link } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import usePermissions from '@/hooks/usePermissions';
import { AlertCircle, ArrowLeft, CheckCircle2, XCircle } from 'lucide-react';

interface ReportRow {
    row: number;
    email: string;
    status: string;
    errors?: string[];
}

interface ImportJob {
    id: number;
    status: string;
    created_at: string | null;
    academy: { id: number; name: string } | null;
    created: number;
    enrolled: number;
    failed: number;
    total: number;
    error?: string | null;
    rows: ReportRow[];
}

interface DetailProps {
    job: ImportJob;
}

const STATUS_LABELS: Record<string, string> = {
    pending: 'Pendiente',
    processing: 'Procesando',
    completed: 'Completada',
    failed: 'Fallida',
};

const formatDate = (value: string | null) => {
    if (!value) return '—';
    return new Date(value).toLocaleString();
};

const StatCard: React.FC<{ label: string; value: number; className?: string }> = ({
    label,
    value,
    className = '',
}) => (
    <div className="rounded-lg bg-white p-4 shadow dark:bg-neutral-800">
        <p className="text-sm text-gray-500 dark:text-neutral-400">{label}</p>
        <p className={`mt-1 text-2xl font-bold ${className}`}>{value}</p>
    </div>
);

const DetailContent: React.FC<DetailProps> = ({ job }) => {
    const { route } = useRoutes();

    return (
        <StaffLayout>
            <div className="mx-auto max-w-5xl">
                <div className="mb-8">
                    <Link
                        href={route('staff-user-import-history').toString()}
                        className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-neutral-300"
                    >
                        <ArrowLeft size={16} className="mr-1" />
                        Volver al historial
                    </Link>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Informe de importación #{job.id}
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-neutral-300">
                        {formatDate(job.created_at)} ·{' '}
                        {STATUS_LABELS[job.status] || job.status}
                        {job.academy ? ` · ${job.academy.name}` : ''}
                    </p>
                </div>

                {job.error && (
                    <div className="mb-6 flex items-center rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                        <AlertCircle size={16} className="mr-2" />
                        {job.error}
                    </div>
                )}

                <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <StatCard label="Total" value={job.total} />
                    <StatCard
                        label="Creados"
                        value={job.created}
                        className="text-green-600"
                    />
                    <StatCard
                        label="Matriculados"
                        value={job.enrolled}
                        className="text-blue-600"
                    />
                    <StatCard
                        label="Con errores"
                        value={job.failed}
                        className="text-red-600"
                    />
                </div>

                <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-neutral-800">
                    <div className="border-b border-gray-100 px-4 py-3 font-semibold text-gray-900 dark:border-neutral-700 dark:text-white">
                        Detalle por fila
                    </div>
                    {job.rows.length === 0 ? (
                        <div className="p-8 text-center text-sm text-gray-500 dark:text-neutral-400">
                            {job.status === 'processing'
                                ? 'La importación se está procesando…'
                                : 'No hay filas registradas.'}
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-neutral-700">
                            <thead>
                                <tr>
                                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-neutral-300">
                                        #
                                    </th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-neutral-300">
                                        Email
                                    </th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-neutral-300">
                                        Resultado
                                    </th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-neutral-300">
                                        Detalle
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-neutral-700">
                                {job.rows.map((row) => {
                                    const isFailed = row.status === 'failed';
                                    return (
                                        <tr key={row.row}>
                                            <td className="px-4 py-3 text-gray-500 dark:text-neutral-400">
                                                {row.row}
                                            </td>
                                            <td className="px-4 py-3 text-gray-700 dark:text-neutral-300">
                                                {row.email || '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span
                                                    className={`inline-flex items-center text-sm ${
                                                        isFailed
                                                            ? 'text-red-600'
                                                            : 'text-green-600'
                                                    }`}
                                                >
                                                    {isFailed ? (
                                                        <XCircle
                                                            size={16}
                                                            className="mr-1"
                                                        />
                                                    ) : (
                                                        <CheckCircle2
                                                            size={16}
                                                            className="mr-1"
                                                        />
                                                    )}
                                                    {row.status === 'created'
                                                        ? 'Creado'
                                                        : row.status === 'enrolled'
                                                          ? 'Matriculado'
                                                          : 'Error'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-gray-600 dark:text-neutral-400">
                                                {(row.errors || []).join(', ') || '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </StaffLayout>
    );
};

const Detail: React.FC<DetailProps> = (props) => {
    const { canImportUsers } = usePermissions();

    if (!canImportUsers) {
        return (
            <ThemeWrapper>
                <StaffLayout>
                    <div className="mx-auto max-w-3xl py-12 text-center">
                        <AlertCircle
                            size={40}
                            className="mx-auto mb-4 text-red-500"
                        />
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                            No tienes permiso para importar usuarios
                        </h1>
                    </div>
                </StaffLayout>
            </ThemeWrapper>
        );
    }

    return (
        <ThemeWrapper>
            <DetailContent {...props} />
        </ThemeWrapper>
    );
};

export default Detail;
